import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import * as XLSX from 'xlsx';
import { useCurrency } from '../contexts/CurrencyContext';
import PlanRenewalModal from '../components/PlanRenewalModal';
import { syncStoreWithServer, getAllFromStore } from '../utils/localDb';
import { getHighAccuracyPosition, isGeolocationAvailable } from '../utils/geolocation';

function StaffPage() {
    const { t } = useLanguage();
    const { formatAmount } = useCurrency();
    const [pin, setPin] = useState('');
    const [staff, setStaff] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem('staffToken') || localStorage.getItem('token'));
    const [attendance, setAttendance] = useState([]);
    const [leaves, setLeaves] = useState([]);
    const [activeTab, setActiveTab] = useState('attendance');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [companyInfo, setCompanyInfo] = useState(null);
    const planFeatures = usePlanFeatures(companyInfo);
    const [isProratedUpgradeOpen, setIsProratedUpgradeOpen] = useState(false);

    // Smart upgrade handler: paid Starter users see proration modal; others navigate to pricing
    const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: () => {} });
    const handleUpgradeClick = () => {
        setConfirmModal({
            show: true,
            title: 'Upgrade Required',
            message: 'This feature is not available in the Starter plan. You need to upgrade the plan to access this feature. Do you want to upgrade the plan?',
            confirmText: 'Upgrade Plan',
            confirmClass: 'btn btn-primary flex-1 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-lg',
            cancelClass: 'btn btn-secondary flex-1 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-lg',
            onConfirm: () => {
                setConfirmModal({ show: false, message: '', onConfirm: () => {} });
                setIsProratedUpgradeOpen(true);
            }
        });
    };
    const [dateFilter, setDateFilter] = useState('today');
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [branches, setBranches] = useState([]);
    const [allStaff, setAllStaff] = useState([]);
    const [selectedBranch, setSelectedBranchState] = useState(() => {
        const saved = localStorage.getItem('selectedBranch_staff');
        return saved ? saved : 'all';
    });
    const setSelectedBranch = (val) => {
        setSelectedBranchState(val);
        localStorage.setItem('selectedBranch_staff', val);
    };
    const [showBranchDropdown, setShowBranchDropdown] = useState(false);
    const [selectedGroupDetails, setSelectedGroupDetails] = useState(null);
    const [staffListModal, setStaffListModal] = useState({ show: false, title: '', staff: [] });
    // const [expandedReason, setExpandedReason] = useState(null);
    const [reasonModal, setReasonModal] = useState({ show: false, reason: '', staffName: '' });
    const [outOfRangeModal, setOutOfRangeModal] = useState({ show: false, message: '' });
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);

    useEffect(() => {
        if (branches && branches.length === 1) {
            const onlyBranchId = branches[0].id.toString();
            if (selectedBranch !== onlyBranchId) {
                setSelectedBranch(onlyBranchId);
            }
        }
    }, [branches, selectedBranch]);

    useEffect(() => {
        let isMounted = true;
        const fetchCompanyInfo = async () => {
            try {
                const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
                    ? 'http://localhost:5000'
                    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

                let url = `${API_URL}/api/company/public`;
                let options = {};

                if (token) {
                    url = `${API_URL}/api/company/profile`;
                    options = {
                        headers: {
                            'Authorization': `Bearer ${token}`
                        }
                    };
                }

                const res = await fetch(url, options);
                const json = await res.json();
                if (isMounted && json.success && json.data) {
                    setCompanyInfo(json.data);
                }
            } catch (err) {
                console.error("Failed to fetch company info", err);
            }
        };
        fetchCompanyInfo();
        return () => { isMounted = false; };
    }, [token]);

    // Click outside to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!event.target.closest('.branch-dropdown-container')) {
                setShowBranchDropdown(false);
            }
            if (!event.target.closest('.filter-dropdown-container')) {
                setShowFilterDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Leave form
    const [leaveData, setLeaveData] = useState({ start_date: '', end_date: '', reason: '', leave_type: 'full' });

    const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
        ? 'http://localhost:5000'
        : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

    const canUseLeaveManagement = planFeatures.isTrial || planFeatures.features.leaveManagement;
    const canExportAttendance = planFeatures.isTrial || planFeatures.features.downloadReports;

    const formatDate = (date) => {
        if (!date) return '';
        const d = new Date(date);
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    const fetchAttendance = useCallback(async (staffId, authToken, role) => {
        try {
            // If admin or manager, fetch all attendance. If staff, fetch specific.
            const userRole = role?.toLowerCase() || '';
            const isAdminOrManager = userRole === 'admin' || userRole.includes('manager');
            let url = isAdminOrManager
                ? `${API_URL}/api/staff/attendance`
                : `${API_URL}/api/staff/attendance/${staffId}`;

            const now = new Date();
            let start = new Date();
            let end = new Date(); // end is today by default

            // "today" -> start=today, end=today
            // "yesterday" -> start=yesterday, end=yesterday
            // "last7" -> start=today-7, end=today
            // "thisMonth" -> start=1st, end=today (or end of month? Usually today is fine for "so far")

            if (dateFilter === 'today') {
                // start and end are already today
            } else if (dateFilter === 'yesterday') {
                start.setDate(now.getDate() - 1);
                end.setDate(now.getDate() - 1);
            } else if (dateFilter === 'last7') {
                start.setDate(now.getDate() - 7);
            } else if (dateFilter === 'thisMonth') {
                start = new Date(now.getFullYear(), now.getMonth(), 1);
            } else if (dateFilter === 'custom' && customStart && customEnd) {
                start = new Date(customStart);
                end = new Date(customEnd);
            } else if (dateFilter === 'custom') {
                return;
            }


            url += `?startDate=${formatDate(start)}&endDate=${formatDate(end)}`;

            const res = await fetch(url, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const json = await res.json();
            if (json.success) {
                setAttendance(json.data);
            } else {
                // No-op: server-side handled error
                console.error('[Attendance Fetch] Failed:', json.message);
            }
        } catch (err) {
            console.error('[Attendance Fetch] Error:', err);
        }
    }, [API_URL, dateFilter, customStart, customEnd]);

    const fetchLeaves = useCallback(async (authToken) => {
        try {
            const res = await fetch(`${API_URL}/api/staff/leaves`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const json = await res.json();
            console.log('Fetched leaves:', json.data);
            if (json.success) {
                setLeaves(json.data);
            }
        } catch (err) {
            console.error(err);
        }
    }, [API_URL]);

    const fetchBranches = useCallback(async (authToken) => {
        try {
            const res = await fetch(`${API_URL}/api/branches`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const json = await res.json();
            if (json.success) {
                setBranches(json.data || []);
            }
        } catch (err) {
            console.error('Failed to fetch branches', err);
        }
    }, [API_URL]);

    const fetchAllStaff = useCallback(async (authToken) => {
        try {
            const res = await fetch(`${API_URL}/api/staff`, {
                headers: { 'Authorization': `Bearer ${authToken}` }
            });
            const json = await res.json();
            if (json.success) {
                setAllStaff(json.data || []);
                try {
                    await syncStoreWithServer('staff', json.data || []);
                } catch (e) {
                    console.error('Failed to cache staff', e);
                }
            }
        } catch (err) {
            console.error('Failed to fetch staff', err);
            try {
                const cached = await getAllFromStore('staff');
                if (cached && cached.length > 0) {
                    setAllStaff(cached);
                }
            } catch (e) {
                console.error('Failed to load cached staff', e);
            }
        }
    }, [API_URL]);

    const handleRefreshData = useCallback(() => {
        if (!staff) return;
        if (activeTab === 'attendance') {
            fetchAttendance(staff.id, token, staff?.role);
        } else if (activeTab === 'leaves') {
            fetchLeaves(token);
        }
    }, [activeTab, staff, token, fetchAttendance, fetchLeaves]);

    const handleLeaveAction = async (id, status) => {
        try {
            const res = await fetch(`${API_URL}/api/staff/leaves/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ status }),
            });
            const json = await res.json();
            if (json.success) {
                setSuccessMsg(`Leave ${status}`);
                fetchLeaves(token);
                setTimeout(() => setSuccessMsg(''), 3000);
            }
        } catch (err) {
            setError('Action failed');
        }
    };

    useEffect(() => {
        if (token) {
            const storedStaff = localStorage.getItem('staffDetails');
            if (storedStaff) {
                const s = JSON.parse(storedStaff);
                setStaff(prev => {
                    if (prev && prev.id === s.id) return prev;
                    return s;
                });
                const userRole = s.role?.toLowerCase() || '';
                if (userRole === 'admin' || userRole.includes('manager')) {
                    fetchBranches(token);
                    fetchAllStaff(token);
                }
                fetchAttendance(s.id, token, s.role);
                fetchLeaves(token);
            } else {
                // Fallback: If no staff details but we have a token, check if it's the Admin token
                const adminToken = localStorage.getItem('token');
                if (token === adminToken) {
                    const adminUser = { id: 0, name: 'Admin', role: 'admin' };
                    setStaff(adminUser);
                    // Fetch all for admin
                    fetchAttendance(0, token, 'admin');
                    fetchLeaves(token);
                    fetchBranches(token);
                    fetchAllStaff(token);
                } else {
                    setToken(null);
                }
            }
        } else {
            // No active staff session? Auto-login if Admin token exists
            const adminToken = localStorage.getItem('token');
            if (adminToken) {
                setToken(adminToken);
            }
        }
    }, [token, fetchAttendance, fetchLeaves, fetchBranches, fetchAllStaff]);

    const groupedAttendance = React.useMemo(() => {
        if (!attendance || attendance.length === 0) return [];

        let filteredAttendance = attendance;
        if (selectedBranch !== 'all') {
            filteredAttendance = attendance.filter(log => log.branch_id === parseInt(selectedBranch));
        }

        const userRole = staff?.role?.toLowerCase() || '';
        const isManageRole = userRole === 'admin' || userRole.includes('manager');
        const staffIdToUse = isManageRole ? 0 : (staff?.id || 0);

        const groups = {};
        filteredAttendance.forEach(log => {
            // If not manager/admin, only show own attendance
            if (staffIdToUse !== 0 && log.staff_id !== staffIdToUse) return;
            const dateStr = formatDate(new Date(log.date));
            const staffKey = log.staff_id;
            const key = `${dateStr}_${staffKey}`;

            if (!groups[key]) {
                groups[key] = {
                    id: key,
                    date: log.date,
                    staff_id: log.staff_id,
                    staff_name: log.staff_name,
                    branch_id: log.branch_id,
                    branch_name: log.branch_name,
                    daily_rate: log.daily_rate || 0,
                    first_in: log.check_in,
                    last_out: log.check_out,
                    total_ms: 0,
                    records: [],
                    is_active: false
                };
            }

            groups[key].records.push(log);

            if (log.check_in && (!groups[key].first_in || new Date(log.check_in) < new Date(groups[key].first_in))) {
                groups[key].first_in = log.check_in;
            }

            if (log.check_out) {
                if (!groups[key].last_out || new Date(log.check_out) > new Date(groups[key].last_out)) {
                    groups[key].last_out = log.check_out;
                }
            } else {
                groups[key].is_active = true;
            }

            if (log.check_in && log.check_out) {
                groups[key].total_ms += (new Date(log.check_out) - new Date(log.check_in));
            }
        });

        if (process.env.NODE_ENV === 'development') {
            console.time('compute_groupedAttendance');
        }

        const result = Object.values(groups).map(g => {
            const hrs = Math.floor(g.total_ms / 3600000);
            const mins = Math.round((g.total_ms % 3600000) / 60000);
            const formatted_date = g.date ? new Date(g.date).toLocaleDateString() : '';
            const first_in_formatted = g.first_in ? new Date(g.first_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
            const last_out_formatted = g.last_out ? new Date(g.last_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '-';
            return {
                ...g,
                formatted_date,
                first_in_formatted,
                last_out_formatted,
                formatted_duration: `${hrs}h ${mins}m`,
                records: g.records.sort((a, b) => new Date(a.check_in) - new Date(b.check_in))
            };
        }).sort((a, b) => new Date(b.date) - new Date(a.date) || b.staff_id - a.staff_id);

        if (process.env.NODE_ENV === 'development') {
            console.timeEnd('compute_groupedAttendance');
            console.log(`[Perf][groupedAttendance] raw:${attendance.length} filtered:${filteredAttendance.length} groups:${result.length}`);
        }

        return result;
    }, [attendance, selectedBranch, staff?.id, staff?.role]);

    const isAdminOrManager = React.useMemo(() => {
        const r = staff?.role?.toLowerCase() || '';
        return r === 'admin' || r.includes('manager');
    }, [staff?.role]);

    const downloadAttendanceReport = () => {
        if (!canExportAttendance) {
            alert('Exporting attendance reports is available on the Premium plan. Please upgrade to continue.');
            return;
        }
        if (!attendance || attendance.length === 0) {
            alert(t('noAttendanceToExport'));
            return;
        }

        const reportData = [];
        const headers = [];
        const isAdmin = isAdminOrManager;
        if (isAdmin) headers.push("Staff Name");
        headers.push("Date", "Start Time", "End Time", "Working Hours", "Late (Mins)", "Half Day", "Daily Rate", "Total Salary", "Branch", "Status");
        reportData.push(headers);

        groupedAttendance.forEach(group => {
            const row = [];
            if (isAdmin) row.push(group.staff_name || 'Unknown');
            
            // Calculate effective salary multiplier based on is_half_day
            const salaryMultiplier = group.is_half_day ? 0.5 : 1;
            const effectiveSalary = (parseFloat(group.daily_rate) || 0) * salaryMultiplier;
            
            row.push(
                new Date(group.date).toLocaleDateString(),
                group.first_in ? new Date(group.first_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-',
                group.last_out ? new Date(group.last_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true }) : '-',
                group.formatted_duration,
                group.is_late ? (group.late_minutes_actual + " mins") : 'No',
                group.is_half_day ? 'Yes' : 'No',
                group.daily_rate || 0,
                formatAmount(effectiveSalary),
                group.branch_name || (group.branch_id ? 'Unknown' : 'All Branches'),
                group.is_active ? 'Active' : 'Completed'
            );
            reportData.push(row);
        });

        // Summary Calculations
        const uniqueDates = new Set();
        groupedAttendance.forEach(g => {
            uniqueDates.add(formatDate(new Date(g.date)));
        });
        const presentDays = uniqueDates.size;

        const now = new Date();
        now.setHours(0, 0, 0, 0);
        let start = new Date(now);
        let end = new Date(now);

        if (dateFilter === 'today') { }
        else if (dateFilter === 'yesterday') {
            start.setDate(now.getDate() - 1);
            end.setDate(now.getDate() - 1);
        }
        else if (dateFilter === 'last7') {
            start.setDate(now.getDate() - 6);
        }
        else if (dateFilter === 'thisMonth') {
            start = new Date(now.getFullYear(), now.getMonth(), 1);
            start.setHours(0, 0, 0, 0);
        }
        else if (dateFilter === 'custom' && customStart && customEnd) {
            start = new Date(customStart);
            end = new Date(customEnd);
        }

        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);

        const diffTime = Math.abs(end - start);
        const totalRangeDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;

        const leavesTakenInPeriod = leaves.filter(leave => {
            const leaveStart = new Date(leave.start_date);
            const leaveEnd = new Date(leave.end_date);
            leaveStart.setHours(0, 0, 0, 0);
            leaveEnd.setHours(0, 0, 0, 0);
            return leave.status === 'approved' &&
                leaveStart <= end && leaveEnd >= start;
        }).reduce((total, leave) => {
            const leaveStart = new Date(leave.start_date);
            const leaveEnd = new Date(leave.end_date);
            leaveStart.setHours(0, 0, 0, 0);
            leaveEnd.setHours(0, 0, 0, 0);
            const actualStart = leaveStart > start ? leaveStart : start;
            const actualEnd = leaveEnd < end ? leaveEnd : end;
            const leaveDays = Math.round((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
            // Calculate leave count: half day = 0.5, full day = 1.0 (default to full if leave_type not set)
            const leaveMultiplier = leave.leave_type === 'half' ? 0.5 : 1.0;
            return total + (leaveDays * leaveMultiplier);
        }, 0);

        // Get working days from company settings if available
        let workingDaysCount = 0;
        let scheduleSettings = companyInfo?.attendance_settings?.schedule;
        let holidays = companyInfo?.attendance_settings?.holidays || [];
        let salaryType = companyInfo?.attendance_settings?.salary_calculation_type || 'daily';
        
        // Calculate the actual number of expected working days in the range
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const dateStr = [d.getFullYear(), String(d.getMonth() + 1).padStart(2, '0'), String(d.getDate()).padStart(2, '0')].join('-');
            if (holidays.includes(dateStr)) continue; // Skip holidays

            const dayName = d.toLocaleDateString('en-US', { weekday: 'short' }); // Mon, Tue, etc.
            // Default to Mon-Fri if no settings, otherwise check schedule
            if (scheduleSettings) {
                if (scheduleSettings[dayName]?.enabled !== false) {
                    workingDaysCount++;
                }
            } else {
                if (dayName !== 'Sat' && dayName !== 'Sun') {
                    workingDaysCount++;
                }
            }
        }
        
        // Use workingDaysCount for absence calculation, fallback to totalRangeDays if something's wrong
        const expectedDays = workingDaysCount > 0 ? workingDaysCount : totalRangeDays;

        const absentDays = Math.max(0, expectedDays - presentDays - leavesTakenInPeriod);

        let totalSalary = 0;
        const staffSummary = {};

        groupedAttendance.forEach(g => {
            const dailyRate = parseFloat(g.daily_rate) || 0;
            if (!staffSummary[g.staff_id]) {
                const staffId = g.staff_id;
                const staffObj = allStaff.find(s => s.id === staffId);
                const monthlySalary = staffObj ? parseFloat(staffObj.salary) || 0 : 0;
                
                const staffLeaves = leaves.filter(leave => {
                    const leaveStart = new Date(leave.start_date);
                    const leaveEnd = new Date(leave.end_date);
                    leaveStart.setHours(0, 0, 0, 0);
                    leaveEnd.setHours(0, 0, 0, 0);
                    return leave.staff_id === staffId &&
                        leave.status === 'approved' &&
                        leaveStart <= end && leaveEnd >= start;
                });

                const sLeavesTaken = staffLeaves.reduce((total, leave) => {
                    const leaveStart = new Date(leave.start_date);
                    const leaveEnd = new Date(leave.end_date);
                    leaveStart.setHours(0, 0, 0, 0);
                    leaveEnd.setHours(0, 0, 0, 0);
                    const actualStart = leaveStart > start ? leaveStart : start;
                    const actualEnd = leaveEnd < end ? leaveEnd : end;
                    const leaveDays = Math.round((actualEnd - actualStart) / (1000 * 60 * 60 * 24)) + 1;
                    const leaveMultiplier = leave.leave_type === 'half' ? 0.5 : 1.0;
                    return total + (leaveDays * leaveMultiplier);
                }, 0);

                staffSummary[g.staff_id] = {
                    name: g.staff_name,
                    dailyRate: dailyRate,
                    monthlySalary: monthlySalary,
                    fullDays: 0,
                    halfDays: 0,
                    leavesTaken: sLeavesTaken,
                    lateDays: 0,
                    totalSalary: 0
                };
            }
            
            // Adjust present days and salary for half day
            if (g.is_half_day) {
                staffSummary[g.staff_id].halfDays++;
                if (salaryType === 'daily') staffSummary[g.staff_id].totalSalary += dailyRate * 0.5;
            } else {
                staffSummary[g.staff_id].fullDays++;
                if (salaryType === 'daily') staffSummary[g.staff_id].totalSalary += dailyRate;
            }
            
            if (g.is_late) {
                staffSummary[g.staff_id].lateDays++;
            }
        });

        Object.values(staffSummary).forEach(s => {
            // Equivalent present days for absence calc (half day = 0.5 present)
            const equivalentPresent = s.fullDays + (s.halfDays * 0.5);
            s.absentDays = Math.max(0, expectedDays - equivalentPresent - s.leavesTaken);
            if (salaryType === 'monthly') {
                const deductAbsent = companyInfo?.attendance_settings?.deduct_absent_from_monthly !== false;
                s.totalSalary = s.monthlySalary;
                if (deductAbsent && s.absentDays > 0) {
                    s.totalSalary = Math.max(0, s.totalSalary - (s.absentDays * s.dailyRate));
                }
            }
            totalSalary += s.totalSalary;
        });

        reportData.push([]);
        reportData.push(["", "SUMMARY"]);
        reportData.push(["Expected Working Days", expectedDays]);
        reportData.push(["Total Days Present", presentDays]); // Raw distinct days present
        reportData.push(["Total Days Absent", absentDays]);
        reportData.push(["Total Leaves Taken", leavesTakenInPeriod]);
        reportData.push(["Total Salary", formatAmount(totalSalary)]);

        if (isAdmin && Object.keys(staffSummary).length > 1) {
            reportData.push([]);
            reportData.push(["", "SALARY BREAKDOWN"]);
            reportData.push(["Staff Name", "Daily Rate", "Full Days", "Half Days", "Late Marks", "Leaves Taken", "Absent", "Total Salary"]);
            Object.values(staffSummary).forEach(s => {
                reportData.push([s.name, s.dailyRate, s.fullDays, s.halfDays, s.lateDays, s.leavesTaken, s.absentDays, formatAmount(s.totalSalary)]);
            });
        }

        const ws = XLSX.utils.aoa_to_sheet(reportData);
        ws['!cols'] = reportData[0].map(() => ({ wch: 18 }));

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");

        const fileName = `Attendance_${staff?.name?.replace(/\s+/g, '_') || 'Report'}_${dateFilter}_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const res = await fetch(`${API_URL}/api/staff/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin })
            });
            const json = await res.json();
            if (json.success) {
                setToken(json.token);
                setStaff(json.staff);
                localStorage.setItem('staffToken', json.token);
                localStorage.setItem('staffDetails', JSON.stringify(json.staff));
                fetchAttendance(json.staff.id, json.token, json.staff.role);
                const userRole = json.staff.role?.toLowerCase();
                if (userRole === 'manager' || userRole === 'admin') {
                    fetchBranches(json.token);
                    fetchAllStaff(json.token);
                }
            } else {
                setError(json.message);
            }
        } catch (err) {
            setError('Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleLogout = () => {
        setToken(null);
        setStaff(null);
        localStorage.removeItem('staffToken');
        localStorage.removeItem('staffDetails');
    };

    const handlePunch = async (type) => {
        setLoading(true);

        const sendPunchRequest = async (latitude = null, longitude = null) => {
            try {
                const res = await fetch(`${API_URL}/api/staff/punch`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ staff_id: staff.id, type, qr_latitude: latitude, qr_longitude: longitude })
                });
                const json = await res.json();
                if (json.success) {
                    setSuccessMsg(json.message);
                    fetchAttendance(staff.id, token, staff?.role);
                    setTimeout(() => setSuccessMsg(''), 3000);
                } else {
                    if (json.code === 'OUTSIDE_ALLOWED_RADIUS') {
                        setOutOfRangeModal({ show: true, message: json.message, distance: json.distance, radius: json.radius, accuracy: json.accuracy });
                    } else if (json.code === 'LOCATION_UNAVAILABLE' || json.code === 'LOCATION_NOT_CONFIGURED') {
                        setOutOfRangeModal({ show: true, message: json.message });
                    } else {
                        setError(json.message);
                        setTimeout(() => setError(''), 3000);
                    }
                }
            } catch (err) {
                setError('Punch failed');
            } finally {
                setLoading(false);
            }
        };

        // Ask the server first without a location. When the global QR restriction
        // is off, it accepts the punch immediately, so GPS is never requested.
        // This prevents a weak-GPS error from blocking unrestricted staff QR use.
        try {
            const res = await fetch(`${API_URL}/api/staff/punch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ staff_id: staff.id, type, qr_latitude: null, qr_longitude: null })
            });
            const json = await res.json();

            if (json.success) {
                setSuccessMsg(json.message);
                fetchAttendance(staff.id, token, staff?.role);
                setTimeout(() => setSuccessMsg(''), 3000);
                setLoading(false);
                return;
            }

            // LOCATION_UNAVAILABLE is only returned when the server-side global
            // restriction is enabled, so obtain GPS only in that case.
            if (json.code !== 'LOCATION_UNAVAILABLE') {
                if (json.code === 'LOCATION_NOT_CONFIGURED' || json.code === 'OUTSIDE_ALLOWED_RADIUS') {
                    setOutOfRangeModal({ show: true, message: json.message, distance: json.distance, radius: json.radius });
                } else {
                    setError(json.message || 'Punch failed');
                }
                setLoading(false);
                return;
            }
        } catch (err) {
            setError('Punch failed');
            setLoading(false);
            return;
        }

        if (!isGeolocationAvailable()) {
            setOutOfRangeModal({ show: true, message: 'This device does not support location access, which is required to punch in/out.' });
            setLoading(false);
            return;
        }

        try {
            const position = await getHighAccuracyPosition({
                cacheKey: `staff:${staff?.company_id || 'company'}:${staff?.branch_id || 'company'}`
            });
            await sendPunchRequest(position.latitude, position.longitude);
        } catch (geoErr) {
            console.warn('Geolocation error:', geoErr && geoErr.code, geoErr && geoErr.message);
            const message = geoErr && geoErr.code === 'TIMEOUT'
                ? 'GPS signal is weak. Please step outside or move near a window and try again.'
                : 'Unable to read your location accurately. Please turn on GPS, step outside and try again.';
            setOutOfRangeModal({ show: true, message });
            setLoading(false);
        }
    };

    const handleLeaveSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            console.log('Submitting leave with data:', { ...leaveData, staff_id: staff.id });
            const res = await fetch(`${API_URL}/api/staff/leaves`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ ...leaveData, staff_id: staff.id })
            });
            const json = await res.json();
            console.log('Leave submission response:', json);
            if (json.success) {
                setSuccessMsg('Leave requested successfully');
                setLeaveData({ start_date: '', end_date: '', reason: '', leave_type: 'full' });
                fetchLeaves(token);
                setTimeout(() => setSuccessMsg(''), 3000);
            } else {
                setError(json.message);
                setTimeout(() => setError(''), 5000);
            }
        } catch (err) {
            console.error('Leave submission error:', err);
            setError('Request failed');
            setTimeout(() => setError(''), 5000);
        } finally {
            setLoading(false);
        }
    };

    if (!token) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
                <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
                    <h2 className="text-2xl font-bold text-center mb-6 text-gray-800">{t('staffPortal')}</h2>
                    {error && <div className="bg-red-100 text-red-700 p-3 rounded mb-4">{error}</div>}
                    <form onSubmit={handleLogin}>
                        <div className="mb-6">
                            <label className="block text-gray-700 mb-2">{t('enterPin')}</label>
                            <input
                                type="password"
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                className="w-full p-3 border rounded-lg text-center text-2xl tracking-widest"
                                maxLength="6"
                                placeholder="••••••"
                                required
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-emerald-600 text-white py-3 rounded-lg font-bold hover:bg-emerald-700 transition"
                        >
                            {loading ? t('verifying') : t('login')}
                        </button>
                    </form>
                </div>
            </div>
        );
    }

    // Filter leaves for display
    const visibleLeaves = staff?.role === 'admin'
        ? leaves // Admin sees all
        : leaves.filter(l => l.staff_id === staff?.id);

    return (
        <>
        <div className="digital-emerald-theme h-screen flex flex-col overflow-hidden bg-gray-50">
            {/* --- Sticky Header --- */}
            <div
                className={`flex-shrink-0 z-50 shadow-lg ${!companyInfo?.banner_url ? 'bg-[#ECFDF5]' : ''}`}
                style={companyInfo?.banner_url ? {
                    backgroundImage: `linear-gradient(rgba(0,0,0,0.78), rgba(0,0,0,0.62)), url(${companyInfo.banner_url})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                } : {}}
            >
                {/* Row 1 - Logo, Title, Actions */}
                <div className={`px-6 md:px-8 pt-5 pb-5 flex items-center justify-between gap-4 border-b ${!companyInfo?.banner_url ? 'border-emerald-100 text-emerald-950' : 'border-white/10 text-white'}`}>
                    <div className="flex items-center gap-3 min-w-0">
                        {companyInfo?.logo_url ? (
                            <div className="bg-white p-1 rounded-xl shadow-sm flex-shrink-0">
                                <img src={companyInfo.logo_url} alt="Logo" className="w-9 h-9 rounded-lg object-cover" />
                            </div>
                        ) : (
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${!companyInfo?.banner_url ? 'bg-emerald-100 text-emerald-600' : 'bg-white/20 text-white'}`}>
                                <i className="fas fa-users text-lg"></i>
                            </div>
                        )}
                        <div className="min-w-0">
                            <h1 className="text-sm md:text-base font-extrabold tracking-tight leading-tight truncate">{companyInfo?.name || t('staffPortal')}</h1>
                            <p className={`text-[11px] font-medium leading-tight ${!companyInfo?.banner_url ? 'text-emerald-600' : 'text-white/70'}`}>{t('staffDashboard')}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                        {staff?.role !== 'admin' && (
                            <button onClick={handleLogout} className="bg-rose-500 hover:bg-rose-600 text-white h-9 px-3 rounded-xl flex items-center gap-1.5 font-bold text-xs shadow-lg active:scale-95 transition-all">
                                <i className="fas fa-sign-out-alt"></i>
                                <span className="hidden sm:inline">{t('logout')}</span>
                            </button>
                        )}
                        {(staff?.role?.toLowerCase() === 'admin' || staff?.role?.toLowerCase().includes('manager')) && (
                            canExportAttendance ? (
                                <button onClick={downloadAttendanceReport} className="h-9 px-3 bg-white hover:bg-emerald-50 text-emerald-600 rounded-xl shadow border border-emerald-200 transition flex items-center gap-2 text-xs font-bold active:scale-95">
                                    <i className="fas fa-file-excel text-sm"></i>
                                    <span>Export Excel</span>
                                </button>
                            ) : (
                                <button onClick={handleUpgradeClick} className="h-9 px-3 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl shadow border border-gray-200 transition flex items-center gap-2 text-xs font-bold active:scale-95">
                                    <i className="fas fa-lock text-sm"></i>
                                    <span>Upgrade to Export</span>
                                </button>
                            )
                        )}
                        <button onClick={handleRefreshData} className="w-9 h-9 flex items-center justify-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl shadow border border-emerald-500 transition active:scale-95 flex-shrink-0" title={t('refresh')}>
                            <i className={`fas fa-sync-alt text-xs ${loading ? 'animate-spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {/* Row 2 - Branch, Tabs, Date Filter Pills */}
                <div className={`px-6 md:px-8 pt-4 pb-4 flex flex-wrap items-center gap-3 ${!companyInfo?.banner_url ? '' : 'text-white'}`}>
                    {/* Branch Selector */}
                    {(staff?.role?.toLowerCase() === 'admin' || staff?.role?.toLowerCase().includes('manager')) && (
                        <div className="relative branch-dropdown-container flex-shrink-0">
                            <button
                                onClick={() => setShowBranchDropdown(!showBranchDropdown)}
                                className="bg-white border border-emerald-200 h-9 px-3 rounded-xl flex items-center gap-2 hover:bg-emerald-50 transition text-emerald-900 shadow-sm text-xs font-bold"
                            >
                                <i className="fas fa-store text-emerald-500 text-[10px]"></i>
                                <span className="truncate max-w-[120px]">
                                    {selectedBranch === 'all' ? t('allBranchesCombined') || 'All Branches' : (branches.find(b => b.id.toString() === selectedBranch.toString())?.name || t('branch'))}
                                </span>
                                <i className={`fas fa-chevron-down text-emerald-400 text-[8px] transition-transform ${showBranchDropdown ? 'rotate-180' : ''}`}></i>
                            </button>
                            {showBranchDropdown && (
                                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-1.5 z-[200] animate-fade-in">
                                    <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 border-b border-gray-100 mb-1">{t('selectBranch')}</p>
                                    {branches.length > 1 && (
                                    <button onClick={() => { setSelectedBranch('all'); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2 ${selectedBranch === 'all' ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}>
                                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedBranch === 'all' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-globe text-[9px]"></i></div>
                                        {t('allBranchesCombined') || 'All Branches'}
                                    </button>
                                    )}
                                    {branches.map(b => (
                                        <button key={b.id} onClick={() => { setSelectedBranch(b.id.toString()); setShowBranchDropdown(false); }} className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2 ${selectedBranch.toString() === b.id.toString() ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}>
                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${selectedBranch.toString() === b.id.toString() ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}><i className="fas fa-store text-[9px]"></i></div>
                                            <span className="truncate">{b.name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Attendance / Leaves Tabs + mobile three-dot on same line */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="bg-white rounded-xl shadow border border-gray-200 p-1 flex gap-1">
                            <button className={`h-7 px-3 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'attendance' ? 'bg-emerald-600 text-white shadow' : 'text-gray-600 hover:text-emerald-700 hover:bg-emerald-50'}`} onClick={() => setActiveTab('attendance')}>
                                <i className="fas fa-clock text-[10px]"></i> {t('attendance')}
                            </button>
                            <button className={`h-7 px-3 rounded-lg font-bold text-xs transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'leaves' ? 'bg-indigo-600 text-white shadow' : 'text-gray-600 hover:text-indigo-700 hover:bg-indigo-50'}`} onClick={() => setActiveTab('leaves')}>
                                <i className="fas fa-calendar-alt text-[10px]"></i> {t('leaves')}
                            </button>
                        </div>

                        {/* ── Mobile three-dot filter (same line as tabs, hidden on sm+) ── */}
                        <div className="relative flex-shrink-0 filter-dropdown-container sm:hidden">
                            <button
                                onClick={() => setShowFilterDropdown(prev => !prev)}
                                className={`h-9 w-9 flex items-center justify-center rounded-xl border shadow font-bold transition-all ${showFilterDropdown ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-white text-gray-600 border-gray-200 hover:bg-emerald-50 hover:text-emerald-700'}`}
                                title="Date Filter"
                            >
                                <i className="fas fa-ellipsis-h text-sm"></i>
                            </button>
                            {showFilterDropdown && (
                                <div className="absolute top-full right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-gray-100 py-1.5 z-[300] animate-fade-in">
                                    <p className="px-3 py-1.5 text-[10px] font-bold text-gray-400 border-b border-gray-100 mb-1">Filter by Date</p>
                                    {[
                                        { key: 'today', icon: 'fa-sun' },
                                        { key: 'yesterday', icon: 'fa-rotate-left' },
                                        { key: 'last7', icon: 'fa-calendar-week' },
                                        { key: 'thisMonth', icon: 'fa-calendar' }
                                    ].map(({ key, icon }) => (
                                        <button
                                            key={key}
                                            onClick={() => { setDateFilter(key); setShowFilterDropdown(false); }}
                                            className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2 ${dateFilter === key ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}
                                        >
                                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${dateFilter === key ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                                <i className={`fas ${icon} text-[9px]`}></i>
                                            </div>
                                            {t(key) || key}
                                        </button>
                                    ))}
                                    {/* Custom — stays open to show date inputs */}
                                    <button
                                        onClick={() => setDateFilter(dateFilter === 'custom' ? 'today' : 'custom')}
                                        className={`w-full text-left px-3 py-2.5 text-xs flex items-center gap-2 ${dateFilter === 'custom' ? 'text-emerald-600 font-bold bg-emerald-50' : 'text-gray-700 font-semibold hover:bg-emerald-50'}`}
                                    >
                                        <div className={`w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0 ${dateFilter === 'custom' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-400'}`}>
                                            <i className="fas fa-calendar-range text-[9px]"></i>
                                        </div>
                                        {t('custom') || 'Custom'}
                                        {dateFilter === 'custom' && customStart && customEnd && (
                                            <span className="ml-auto text-emerald-500 text-[9px] font-bold">{customStart.slice(5)} → {customEnd.slice(5)}</span>
                                        )}
                                    </button>
                                    {/* Date inputs appear inline when Custom is selected — dropdown stays open */}
                                    {dateFilter === 'custom' && (
                                        <div className="px-3 pb-3 pt-1 border-t border-emerald-100 mt-1 space-y-2">
                                            <div>
                                                <span className="block text-[9px] font-bold text-gray-400 mb-1">{t('from') || 'From'}</span>
                                                <input
                                                    type="date"
                                                    value={customStart}
                                                    onChange={e => setCustomStart(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-full text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1.5 [color-scheme:light]"
                                                />
                                            </div>
                                            <div>
                                                <span className="block text-[9px] font-bold text-gray-400 mb-1">{t('to') || 'To'}</span>
                                                <input
                                                    type="date"
                                                    value={customEnd}
                                                    onChange={e => setCustomEnd(e.target.value)}
                                                    onClick={e => e.stopPropagation()}
                                                    className="w-full text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1.5 [color-scheme:light]"
                                                />
                                            </div>
                                            <button
                                                onClick={() => setShowFilterDropdown(false)}
                                                className="w-full py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-bold"
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Date Filter — desktop pills pushed right (hidden on xs) */}
                    <div className="hidden sm:flex items-center gap-2 flex-shrink-0 ml-auto">
                        <div className="bg-white shadow border border-gray-100 rounded-xl p-1 flex gap-0.5">
                            {['today', 'yesterday', 'last7', 'thisMonth'].map((filter) => (
                                <button key={filter} onClick={() => setDateFilter(filter)} className={`h-7 px-2.5 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${dateFilter === filter ? 'bg-emerald-600 text-white shadow' : 'text-gray-500 hover:text-emerald-700 hover:bg-emerald-50'}`}>
                                    {t(filter) || filter}
                                </button>
                            ))}
                        </div>
                        {/* Desktop Custom button */}
                        <div className="relative flex-shrink-0">
                            <button onClick={() => setDateFilter(dateFilter === 'custom' ? 'today' : 'custom')} className={`h-7 px-3 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 border shadow whitespace-nowrap ${dateFilter === 'custom' ? 'bg-emerald-600 text-white border-emerald-500' : 'bg-white text-gray-500 border-gray-200 hover:text-emerald-700 hover:bg-emerald-50'}`}>
                                <i className="fas fa-calendar-range text-[10px]"></i>
                                {t('custom') || 'Custom'}
                                {dateFilter === 'custom' && customStart && customEnd && <span className="text-emerald-200 text-[9px] ml-1">{customStart.slice(5)} {'→'} {customEnd.slice(5)}</span>}
                            </button>
                            {dateFilter === 'custom' && (
                                <div className="absolute top-full right-0 mt-2 bg-white border border-emerald-200 rounded-xl shadow-2xl p-3 z-[200] animate-fade-in flex items-center gap-3 whitespace-nowrap">
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-bold text-gray-400">{t('from') || 'From'}</span>
                                        <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1 [color-scheme:light]" />
                                    </div>
                                    <i className="fas fa-arrow-right text-emerald-400 text-[8px] mt-3"></i>
                                    <div className="flex flex-col gap-0.5">
                                        <span className="text-[9px] font-bold text-gray-400">{t('to') || 'To'}</span>
                                        <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="text-xs font-bold outline-none text-emerald-900 bg-emerald-50 border border-emerald-100 rounded-lg px-2 py-1 [color-scheme:light]" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="container mx-auto px-4 pt-2">
                {successMsg && <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl border border-emerald-100 flex items-center gap-3 mb-4 animate-fade-in">
                    <i className="fas fa-check-circle"></i> {successMsg}
                </div>}
            </div>

            <div className="p-6">
                {staff && staff?.role?.toLowerCase() !== 'admin' && (
                    <div className="bg-white border border-gray-200/80 p-4 rounded-2xl shadow-sm flex items-center justify-between gap-4 mb-6">
                        <div className="flex items-center gap-3 min-w-0">
                            <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center flex-shrink-0">
                                <i className="fas fa-user-circle text-xl"></i>
                            </div>
                            <div className="min-w-0">
                                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider leading-none mb-1">{t('staffName') || 'Staff Name'}</p>
                                <h2 className="text-base font-black text-gray-800 truncate leading-tight">{staff.name}</h2>
                            </div>
                        </div>
                        <span className="px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100">
                            {staff.role || 'Staff'}
                        </span>
                    </div>
                )}
                {activeTab === 'attendance' && (
                    <div className="space-y-8 expand-in" style={{ animationDelay: '0.1s' }}>
                        {(() => {
                            const isPunchedIn = attendance && attendance.some(log => log.staff_id === staff?.id && !log.check_out);
                            return staff && staff?.role?.toLowerCase() !== 'admin' && (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 px-2 sm:px-0">
                                    <button
                                        onClick={() => handlePunch('in')}
                                        disabled={isPunchedIn}
                                        className={`p-4 rounded-xl shadow-lg transition-all duration-300 flex flex-col items-center justify-center gap-2 group border-0 ${
                                            isPunchedIn 
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' 
                                            : 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white hover:shadow-emerald-200 hover:-translate-y-1'
                                        }`}
                                    >
                                        <div className={`p-3 rounded-lg transition-transform ${isPunchedIn ? 'bg-gray-200 text-gray-400' : 'bg-white/20 group-hover:scale-110'}`}>
                                            <i className="fas fa-sign-in-alt text-2xl"></i>
                                        </div>
                                        <span className="font-bold text-base tracking-wider">{t('punchIn')}</span>
                                    </button>
                                    <button
                                        onClick={() => handlePunch('out')}
                                        disabled={!isPunchedIn}
                                        className={`p-4 rounded-xl shadow-lg transition-all duration-300 flex flex-col items-center justify-center gap-2 group border-0 ${
                                            !isPunchedIn 
                                            ? 'bg-gray-100 text-gray-400 cursor-not-allowed border border-gray-200' 
                                            : 'bg-gradient-to-br from-rose-500 to-rose-600 text-white hover:shadow-rose-200 hover:-translate-y-1'
                                        }`}
                                    >
                                        <div className={`p-3 rounded-lg transition-transform ${!isPunchedIn ? 'bg-gray-200 text-gray-400' : 'bg-white/20 group-hover:scale-110'}`}>
                                            <i className="fas fa-sign-out-alt text-2xl"></i>
                                        </div>
                                        <span className="font-bold text-base tracking-wider">{t('punchOut')}</span>
                                    </button>
                                </div>
                            );
                        })()}


                        {/* Summary Cards - Only for admin/manager */}
                        {(staff?.role?.toLowerCase() === 'admin' || staff?.role?.toLowerCase().includes('manager')) && (
                            <div className="flex flex-row flex-nowrap overflow-x-auto no-scrollbar items-stretch gap-4 mb-10">
                                {[
                                    {
                                        title: t('present') || 'Present',
                                        value: (() => {
                                            const todayStr = formatDate(new Date());
                                            return new Set(attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch))).map(log => log.staff_id)).size;
                                        })(),
                                        icon: 'fa-user-check',
                                        color: 'emerald',
                                        onClick: () => {
                                            const todayStr = formatDate(new Date());
                                            const presentStaffIds = new Set(attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch))).map(log => log.staff_id));
                                            const presentStaff = allStaff.filter(s => presentStaffIds.has(s.id) && (selectedBranch === 'all' || s.branch_id === parseInt(selectedBranch)));
                                            setStaffListModal({ show: true, title: `${t('present')} ${t('staff')} (${presentStaff.length})`, staff: presentStaff });
                                        }
                                    },
                                    {
                                        title: (() => {
                                            const isHoliday = (companyInfo?.attendance_settings?.holidays || []).includes(formatDate(new Date()));
                                            return isHoliday ? 'Holiday' : (t('absent') || 'Absent');
                                        })(),
                                        value: (() => {
                                            const isHoliday = (companyInfo?.attendance_settings?.holidays || []).includes(formatDate(new Date()));
                                            if (isHoliday) return 0;
                                            const todayStr = formatDate(new Date());
                                            const presentCount = new Set(attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch))).map(log => log.staff_id)).size;
                                            const totalInBranch = allStaff.filter(s => selectedBranch === 'all' || s.branch_id === parseInt(selectedBranch)).length;
                                            return Math.max(0, totalInBranch - presentCount);
                                        })(),
                                        icon: (() => {
                                            const isHoliday = (companyInfo?.attendance_settings?.holidays || []).includes(formatDate(new Date()));
                                            return isHoliday ? 'fa-glass-cheers' : 'fa-user-times';
                                        })(),
                                        color: (() => {
                                            const isHoliday = (companyInfo?.attendance_settings?.holidays || []).includes(formatDate(new Date()));
                                            return isHoliday ? 'blue' : 'rose';
                                        })(),
                                        onClick: () => {
                                            const isHoliday = (companyInfo?.attendance_settings?.holidays || []).includes(formatDate(new Date()));
                                            if (isHoliday) {
                                                setStaffListModal({ show: true, title: 'Holiday (Everyone Off)', staff: [] });
                                                return;
                                            }
                                            const todayStr = formatDate(new Date());
                                            const presentStaffIds = new Set(attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch))).map(log => log.staff_id));
                                            const absentStaff = allStaff.filter(s => !presentStaffIds.has(s.id) && (selectedBranch === 'all' || s.branch_id === parseInt(selectedBranch)));
                                            setStaffListModal({ show: true, title: `${t('absent')} ${t('staff')} (${absentStaff.length})`, staff: absentStaff });
                                        }
                                    },
                                    {
                                        title: t('totalHours') || 'Total Hours',
                                        value: (() => {
                                            const todayStr = formatDate(new Date());
                                            return attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch))).reduce((acc, log) => acc + (parseFloat(log.duration_hours) || 0), 0).toFixed(1);
                                        })(),
                                        icon: 'fa-clock',
                                        color: 'teal'
                                    },
                                    {
                                        title: t('avgHours') || 'Avg Hours',
                                        value: (() => {
                                            const todayStr = formatDate(new Date());
                                            const filtered = attendance.filter(log => formatDate(log.date) === todayStr && (selectedBranch === 'all' || log.branch_id === parseInt(selectedBranch)));
                                            const count = new Set(filtered.map(l => l.staff_id)).size;
                                            const total = filtered.reduce((acc, log) => acc + (parseFloat(log.duration_hours) || 0), 0);
                                            return count > 0 ? (total / count).toFixed(1) : '0';
                                        })(),
                                        icon: 'fa-chart-pie',
                                        color: 'amber'
                                    }
                                ].map((metric, idx) => (
                                    <div key={idx}
                                        onClick={metric.onClick}
                                        className={`flex-1 min-w-[200px] px-6 py-5 flex items-center gap-4 rounded-[2rem] shadow-sm border transition-all duration-300 ${metric.onClick ? 'cursor-pointer hover:shadow-lg hover:-translate-y-1' : ''} 
                                                ${metric.color === 'emerald' ? 'bg-emerald-100 border-emerald-200 text-emerald-900 hover:bg-emerald-200' : ''}
                                                ${metric.color === 'rose' ? 'bg-rose-100 border-rose-200 text-rose-900 hover:bg-rose-200' : ''}
                                                ${metric.color === 'teal' ? 'bg-teal-100 border-teal-200 text-teal-900 hover:bg-teal-200' : ''}
                                                ${metric.color === 'amber' ? 'bg-amber-100 border-amber-200 text-amber-900 hover:bg-amber-200' : ''}
                                            `}
                                    >
                                        <div className={`w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-${metric.color}-600 flex-shrink-0 shadow-sm border border-${metric.color}-100`}>
                                            <i className={`fas ${metric.icon} text-lg`}></i>
                                        </div>
                                        <div className="flex flex-col min-w-0">
                                            <span className={`text-sm font-semibold text-gray-500 tracking-normal leading-none mb-1.5 opacity-80`}>{metric.title}</span>
                                            <span className="text-3xl font-bold tracking-tighter truncate">{metric.value}</span>
                                            {metric.onClick && <span className="text-xs font-bold mt-0.5 tracking-normal text-emerald-600 hover:underline">{t('viewList')}</span>}
                                        </div>
                                    </div>
                                 ))}
                             </div>
                         )}
                         <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
                             <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
                                 <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100">
                                     <i className="fas fa-history text-sm"></i>
                                 </div>
                                 {staff?.role?.toLowerCase() === 'admin' ? t('allStaffAttendance') : t('attendanceHistory')}
                             </h3>
                         </div>

                        <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead className="bg-gray-50 border-b border-gray-100">
                                        <tr>
                                            {isAdminOrManager && <th className="p-4 font-semibold text-gray-600">{t('staffName')}</th>}
                                            <th className="p-4 font-semibold text-gray-600">{t('date')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('firstIn')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('lastOut')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('workingHours')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('dailyRate')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('branch')}</th>
                                            <th className="p-4 font-semibold text-gray-600">{t('status')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 text-sm">
                                        {groupedAttendance.length === 0 ? (
                                            <tr>
                                                <td colSpan={isAdminOrManager ? 8 : 7} className="p-8">
                                                    <div className="flex flex-col items-center gap-2">
                                                        <i className="fas fa-calendar-times text-red-200 text-4xl mb-3"></i>
                                                        <p className="text-red-600 font-normal">{t('noAttendanceRecords') || 'No Attendance Records Found'}</p>
                                                        <p className="text-sm text-gray-400">
                                                            {attendance.length === 0
                                                                ? 'No attendance data available. Staff need to punch in first.'
                                                                : `${attendance.length} raw records found but filtered to 0. Check date/branch filters.`}
                                                        </p>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            (() => {
                                                if (process.env.NODE_ENV === 'development') console.time('render_attendanceRows');
                                                const rows = groupedAttendance.map(group => (
                                                    <tr key={group.id} className="hover:bg-gray-50 transition-colors">
                                                        {isAdminOrManager && <td className="p-4 font-medium text-gray-900">{group.staff_name || 'Unknown'}</td>}
                                                        <td className="p-4 text-gray-600">{group.formatted_date}</td>
                                                        <td className="p-4 text-gray-600 font-mono">{group.first_in_formatted}</td>
                                                        <td className="p-4 text-gray-600 font-mono">{group.last_out_formatted}</td>
                                                        <td
                                                            className="p-4 font-mono text-emerald-600 font-medium cursor-pointer hover:underline"
                                                            onClick={() => setSelectedGroupDetails(group)}
                                                            title="Click to view sessions"
                                                        >
                                                            {group.formatted_duration || '-'}
                                                        </td>
                                                        <td className="p-4 text-gray-700 font-semibold">{formatAmount(group.daily_rate || 0)}</td>
                                                        <td className="p-4 text-gray-600">{group.branch_name ? t(group.branch_name) : (group.branch_id ? t('unknown') : t('allBranches'))}</td>
                                                        <td className="p-4">
                                                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${!group.is_active ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' : 'bg-amber-100 text-amber-800 border border-amber-200 animate-pulse'}`}>
                                                                <span className={`w-1.5 h-1.5 rounded-full mr-2 ${!group.is_active ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
                                                                {!group.is_active ? t('completed') : t('active')}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ));
                                                if (process.env.NODE_ENV === 'development') console.timeEnd('render_attendanceRows');
                                                return rows;
                                            })()
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === 'leaves' && (
                    <div className="fade-in space-y-8 px-2 sm:px-0">
                        {!canUseLeaveManagement ? (
                            <div className="bg-white rounded-3xl border border-gray-200 shadow-lg p-10 text-center">
                                <div className="flex items-center justify-center mb-4">
                                    <div className="w-14 h-14 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-2xl">
                                        <i className="fas fa-lock"></i>
                                    </div>
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900 mb-3">Leave Management on Premium</h3>
                                <p className="text-gray-600 mb-6 max-w-xl mx-auto">Your current plan allows staff attendance tracking, but leave requests and leave history require the Premium plan.</p>
                                <button onClick={handleUpgradeClick} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 rounded-xl font-bold transition-all duration-200">
                                    Upgrade to Premium
                                </button>
                            </div>
                        ) : (
                        <>
                            {staff && staff?.role?.toLowerCase() !== 'admin' && (
                                <div className="bg-emerald-50/50 p-6 rounded-xl border border-emerald-100">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
                                        <i className="fas fa-envelope-open-text text-xl"></i>
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-800">{t('newLeaveRequest')}</h3>
                                </div>
                                <form onSubmit={handleLeaveSubmit} className="space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div className="group">
                                            <label className="block text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
                                                <i className="far fa-calendar-alt text-emerald-500"></i> {t('from')}
                                            </label>
                                            <input
                                                type="date"
                                                required
                                                className="w-full px-4 py-3 rounded-xl border-2 border-emerald-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all duration-300 bg-white shadow-sm hover:border-emerald-200"
                                                value={leaveData.start_date}
                                                onChange={e => setLeaveData({ ...leaveData, start_date: e.target.value })}
                                            />
                                        </div>
                                        <div className="group">
                                            <label className="block text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
                                                <i className="far fa-calendar-check text-emerald-500"></i> {t('to')}
                                            </label>
                                            <input
                                                type="date"
                                                required
                                                className="w-full px-4 py-3 rounded-xl border-2 border-emerald-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all duration-300 bg-white shadow-sm hover:border-emerald-200"
                                                value={leaveData.end_date}
                                                onChange={e => setLeaveData({ ...leaveData, end_date: e.target.value })}
                                            />
                                        </div>
                                    </div>
                                    <div className="group">
                                        <label className="block text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
                                            <i className="fas fa-clock text-emerald-500"></i> {t('leaveType') || 'Leave Type'}
                                        </label>
                                        <div className="grid grid-cols-2 gap-4">
                                            <button
                                                type="button"
                                                onClick={() => setLeaveData({ ...leaveData, leave_type: 'full' })}
                                                className={`px-4 py-3 rounded-xl border-2 transition-all duration-300 font-bold flex items-center justify-center gap-2 ${
                                                    leaveData.leave_type === 'full'
                                                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-200'
                                                        : 'bg-white text-gray-700 border-emerald-100 hover:border-emerald-300'
                                                }`}
                                            >
                                                <i className="fas fa-sun"></i>
                                                <span>{t('fullDay') || 'Full Day'}</span>
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setLeaveData({ ...leaveData, leave_type: 'half' })}
                                                className={`px-4 py-3 rounded-xl border-2 transition-all duration-300 font-bold flex items-center justify-center gap-2 ${
                                                    leaveData.leave_type === 'half'
                                                        ? 'bg-amber-600 text-white border-amber-600 shadow-lg shadow-amber-200'
                                                        : 'bg-white text-gray-700 border-emerald-100 hover:border-emerald-300'
                                                }`}
                                            >
                                                <i className="fas fa-adjust"></i>
                                                <span>{t('halfDay') || 'Half Day'}</span>
                                            </button>
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                            <i className="fas fa-info-circle"></i>
                                            {leaveData.leave_type === 'full' 
                                                ? (t('fullDayInfo') || 'Full day leave counts as 1.0 day')
                                                : (t('halfDayInfo') || 'Half day leave counts as 0.5 day')
                                            }
                                        </p>
                                    </div>
                                    <div className="group">
                                        <label className="block text-sm font-bold text-emerald-900 mb-2 flex items-center gap-2">
                                            <i className="fas fa-comment-dots text-emerald-500"></i> {t('reason')}
                                        </label>
                                        <textarea
                                            required
                                            className="w-full px-4 py-3 rounded-xl border-2 border-emerald-100 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/10 outline-none transition-all duration-300 bg-white shadow-sm hover:border-emerald-200 min-h-[120px] resize-none"
                                            placeholder="Write down the reason for your leave request here..."
                                            value={leaveData.reason}
                                            onChange={e => setLeaveData({ ...leaveData, reason: e.target.value })}
                                        ></textarea>
                                    </div>
                                    <div className="pt-4">
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="w-full md:w-auto bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white px-10 py-3.5 rounded-xl shadow-lg shadow-emerald-200 hover:shadow-emerald-300 transition-all duration-300 font-bold flex items-center justify-center gap-2 transform active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            <i className={`fas ${loading ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i> 
                                            {loading ? (t('submitting') || 'Submitting...') : (t('submitRequest') || 'Submit Request')}
                                        </button>
                                        {error && <div className="bg-rose-50 text-rose-700 p-3 rounded-xl border border-rose-100 flex items-center gap-2 mt-4 animate-fade-in">
                                            <i className="fas fa-exclamation-circle"></i> 
                                            <span className="text-sm font-medium">{error}</span>
                                        </div>}
                                    </div>
                                </form>
                            </div>
                        )}
                        </>
                        )}

                        <div>
                            <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
                                <i className="fas fa-list-ul text-emerald-500"></i> {staff?.role === 'admin' ? t('allLeaveRequests') : t('myLeaveHistory')}
                            </h3>
                            
                            {visibleLeaves.length === 0 ? (
                                <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    <i className="fas fa-file-signature text-red-200 text-3xl mb-2"></i>
                                    <p className="text-red-600 font-normal">{t('noLeaveRequestsFound')}</p>
                                </div>
                            ) : (
                                <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-left">
                                            <thead className="bg-gray-50 border-b border-gray-100">
                                                <tr>
                                                    <th className="p-4 font-semibold text-gray-600">{t('staffName') || 'Staff Name'}</th>
                                                    <th className="p-4 font-semibold text-gray-600">{t('reason') || 'Reason'}</th>
                                                    <th className="p-4 font-semibold text-gray-600">{t('from') || 'From'}</th>
                                                    <th className="p-4 font-semibold text-gray-600">{t('to') || 'To'}</th>
                                                    <th className="p-4 font-semibold text-gray-600">{t('leaveType') || 'Type'}</th>
                                                    <th className="p-4 font-semibold text-gray-600">{t('status') || 'Status'}</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100 text-sm">
                                                {visibleLeaves.map(leave => (
                                                    <tr key={leave.id} className="hover:bg-gray-50 transition-colors">
                                                        <td className="p-4 font-medium text-gray-900">{leave.staff_name || staff?.name}</td>
                                                        <td className="p-4 text-gray-600 max-w-xs">
                                                            {leave.reason && leave.reason.length > 50 ? (
                                                                <div>
                                                                    <div className="line-clamp-2">{leave.reason}</div>
                                                                    <button
                                                                        onClick={() => setReasonModal({ show: true, reason: leave.reason, staffName: leave.staff_name || staff?.name })}
                                                                        className="text-emerald-600 hover:text-emerald-700 text-xs font-bold mt-1 flex items-center gap-1"
                                                                    >
                                                                        <i className="fas fa-eye"></i> View Full Reason
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                <div>{leave.reason}</div>
                                                            )}
                                                        </td>
                                                        <td className="p-4 text-gray-600 whitespace-nowrap">
                                                            {new Date(leave.start_date).toLocaleDateString()}
                                                        </td>
                                                        <td className="p-4 text-gray-600 whitespace-nowrap">
                                                            {new Date(leave.end_date).toLocaleDateString()}
                                                        </td>
                                                        <td className="p-4">
                                                             <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold tracking-normal ${
                                                                 (leave.leave_type === 'half' || leave.leave_type === 'Half Day')
                                                                     ? 'bg-amber-100 text-amber-700 border border-amber-200' 
                                                                     : 'bg-blue-100 text-blue-700 border border-blue-200'
                                                             }`}>
                                                                 <i className={`fas ${(leave.leave_type === 'half' || leave.leave_type === 'Half Day') ? 'fa-adjust' : 'fa-sun'} mr-1`}></i>
                                                                 {(leave.leave_type === 'half' || leave.leave_type === 'Half Day') ? (t('halfDay') || 'Half') : (t('fullDay') || 'Full')}
                                                             </span>
                                                        </td>
                                                        <td className="p-4">
                                                            {leave.status === 'pending' && (staff?.role?.toLowerCase() === 'admin' || staff?.role?.toLowerCase() === 'manager') ? (
                                                                <div className="flex items-center gap-2">
                                                                    <button 
                                                                        onClick={() => handleLeaveAction(leave.id, 'approved')} 
                                                                        className="bg-green-100 text-green-700 hover:bg-green-200 px-3 py-1.5 rounded-lg transition font-bold text-xs flex items-center gap-1"
                                                                        title="Approve"
                                                                    >
                                                                        <i className="fas fa-check"></i>
                                                                        <span>Approve</span>
                                                                    </button>
                                                                    <button 
                                                                        onClick={() => handleLeaveAction(leave.id, 'rejected')} 
                                                                        className="bg-red-100 text-red-700 hover:bg-red-200 px-3 py-1.5 rounded-lg transition font-bold text-xs flex items-center gap-1"
                                                                        title="Reject"
                                                                    >
                                                                        <i className="fas fa-times"></i>
                                                                        <span>Reject</span>
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                 <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold tracking-normal border capitalize ${
                                                                     leave.status === 'approved' ? 'bg-green-50 text-green-700 border-green-200' :
                                                                     leave.status === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                                                                     'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                                 }`}>
                                                                     {t(leave.status) || leave.status}
                                                                 </span>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* Session Details Modal */}
            {
                selectedGroupDetails && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setSelectedGroupDetails(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">{t('workSessionDetails')}</h3>
                                    <p className="text-sm text-gray-500">{selectedGroupDetails.staff_name} • {new Date(selectedGroupDetails.date).toLocaleDateString()}</p>
                                </div>
                                <button onClick={() => setSelectedGroupDetails(null)} className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-gray-100 rounded-full">
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="space-y-4">
                                    {selectedGroupDetails.records.map((record, index) => (
                                        <div key={record.id} className="flex items-center gap-4 bg-gray-50 p-4 rounded-xl border border-gray-100">
                                            <div className="bg-emerald-100 text-emerald-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 font-bold">
                                                {index + 1}
                                            </div>
                                            <div className="flex-1 grid grid-cols-2 gap-4">
                                                <div>
                                                    <p className="text-xs text-gray-500 font-bold mb-1">{t('punchIn')}</p>
                                                    <p className="font-mono font-bold text-gray-700">
                                                        {record.check_in ? new Date(record.check_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '-'}
                                                    </p>
                                                </div>
                                                <div>
                                                    <p className="text-xs text-gray-500 font-bold mb-1">{t('punchOut')}</p>
                                                    <p className="font-mono font-bold text-gray-700">
                                                        {record.check_out ? new Date(record.check_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : (
                                                            <span className="text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded text-[10px]">{t('active')}</span>
                                                        )}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-xs text-gray-500 font-bold mb-1">{t('duration')}</p>
                                                <p className="font-mono font-bold text-emerald-600 italic">
                                                    {record.duration || '-'}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-8 bg-emerald-600 rounded-xl p-4 text-white flex justify-between items-center shadow-lg shadow-emerald-200">
                                    <div className="flex items-center gap-3">
                                        <i className="fas fa-history text-emerald-200 text-xl"></i>
                                        <span className="font-bold">{t('totalWorkingHours')}</span>
                                    </div>
                                    <span className="text-2xl font-black font-mono tracking-tight">{selectedGroupDetails.formatted_duration}</span>
                                </div>
                            </div>
                            <div className="p-6 bg-gray-50 border-t border-gray-100 text-center">
                                <button
                                    onClick={() => setSelectedGroupDetails(null)}
                                    className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition shadow-sm"
                                >
                                    {t('closeDetails')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Staff List Modal */}
            {
                staffListModal.show && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setStaffListModal({ show: false, title: '', staff: [] })}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-emerald-50 to-emerald-100">
                                <div>
                                    <h3 className="text-xl font-bold text-gray-800">{staffListModal.title}</h3>
                                    <p className="text-sm text-gray-500">{t('today')} • {new Date().toLocaleDateString()}</p>
                                </div>
                                <button onClick={() => setStaffListModal({ show: false, title: '', staff: [] })} className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-white rounded-full">
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                            <div className="p-6 overflow-y-auto max-h-[60vh]">
                                {staffListModal.staff.length === 0 ? (
                                    <div className="text-center py-12">
                                        <i className="fas fa-users-slash text-red-200 text-4xl mb-3"></i>
                                        <p className="text-red-600 font-normal">{t('noStaffFound') || 'No staff found'}</p>
                                    </div>
                                ) : (
                                    <div className="grid gap-3">
                                        {staffListModal.staff.map((s) => (
                                            <div key={s.id} className="flex items-center gap-4 bg-gradient-to-r from-gray-50 to-white p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all">
                                                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center text-white font-bold text-lg shadow-lg">
                                                    {s.name?.charAt(0).toUpperCase() || 'S'}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-bold text-gray-900 text-lg">{s.name}</p>
                                                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                                                        <span className="flex items-center gap-1">
                                                            <i className="fas fa-user-tag text-emerald-500"></i>
                                                            {s.role}
                                                        </span>
                                                        {s.branch_name && (
                                                            <span className="flex items-center gap-1">
                                                                <i className="fas fa-store text-emerald-500"></i>
                                                                {t(s.branch_name)}
                                                            </span>
                                                        )}
                                                        <span className="flex items-center gap-1">
                                                            <i className="fas fa-phone text-emerald-500"></i>
                                                            {s.phone || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    {staffListModal.title.toLowerCase().includes('present') ? (
                                                        <span className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-xs font-bold">
                                                            <i className="fas fa-check-circle mr-1"></i>
                                                            {t('present')}
                                                        </span>
                                                    ) : staffListModal.title.toLowerCase().includes('holiday') ? (
                                                        <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-bold">
                                                            <i className="fas fa-glass-cheers mr-1"></i>
                                                            Holiday
                                                        </span>
                                                    ) : (
                                                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-bold">
                                                            <i className="fas fa-times-circle mr-1"></i>
                                                            {t('absent')}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <div className="p-6 bg-gray-50 border-t border-gray-100 text-center">
                                <button
                                    onClick={() => setStaffListModal({ show: false, title: '', staff: [] })}
                                    className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition shadow-sm"
                                >
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Out of Range Error Modal */}
            {outOfRangeModal.show && (
                <div className="fixed inset-0 modal-backdrop z-[110] flex items-center justify-center p-4 bg-black/70 backdrop-blur-md transition-all">
                    <div className="bg-white rounded-3xl max-w-sm w-full p-8 shadow-2xl text-center transform scale-100 animate-fade-in-up flex flex-col items-center">
                        <div className="w-20 h-20 bg-red-100 text-red-500 rounded-full flex items-center justify-center mb-6 shadow-inner">
                            <i className="fas fa-map-marker-alt text-4xl animate-bounce"></i>
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">Out of Range</h3>
                        <p className="text-gray-600 mb-8 font-medium leading-relaxed">{outOfRangeModal.message}</p>
                        <button 
                            onClick={() => setOutOfRangeModal({ show: false, message: '' })}
                            className="w-full bg-gray-900 hover:bg-black text-white py-3.5 rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all active:scale-95"
                        >
                            Got it
                        </button>
                    </div>
                </div>
            )}

            {/* Confirm Modal */}
            {confirmModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setConfirmModal({ show: false })}>
                    <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-6 text-center" onClick={e => e.stopPropagation()}>
                        <div className="w-16 h-16 bg-indigo-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <i className="fas fa-crown text-2xl text-indigo-500"></i>
                        </div>
                        <h3 className="text-xl font-bold mb-2 text-gray-900">{confirmModal.title || 'Confirm Action'}</h3>
                        <p className="mb-6 text-gray-500 text-sm leading-relaxed">{confirmModal.message}</p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmModal({ show: false })} className={confirmModal.cancelClass || "btn btn-secondary flex-1 py-2"}>Cancel</button>
                            <button onClick={confirmModal.onConfirm} className={confirmModal.confirmClass || "btn btn-danger flex-1 py-2"}>{confirmModal.confirmText || 'Yes'}</button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Reason Modal */}
            {
                reasonModal.show && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={() => setReasonModal({ show: false, reason: '', staffName: '' })}>
                        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200" onClick={e => e.stopPropagation()}>
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-indigo-100">
                                <div className="flex-1 min-w-0 pr-4">
                                    <h3 className="text-xl font-bold text-gray-800">{t('leaveReason') || 'Leave Reason'}</h3>
                                    <p className="text-sm text-gray-500 truncate">{reasonModal.staffName}</p>
                                </div>
                                <button onClick={() => setReasonModal({ show: false, reason: '', staffName: '' })} className="text-gray-400 hover:text-gray-600 transition p-2 hover:bg-white rounded-full flex-shrink-0">
                                    <i className="fas fa-times text-xl"></i>
                                </button>
                            </div>
                            <div className="p-6 max-h-[60vh] overflow-y-auto">
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                                    <div className="flex items-start gap-3">
                                        <div className="bg-indigo-100 text-indigo-600 w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                                            <i className="fas fa-comment-dots"></i>
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm text-gray-700 leading-relaxed break-words whitespace-pre-wrap">{reasonModal.reason}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 bg-gray-50 border-t border-gray-100 text-center">
                                <button
                                    onClick={() => setReasonModal({ show: false, reason: '', staffName: '' })}
                                    className="w-full py-3 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition shadow-sm"
                                >
                                    {t('close')}
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
            </div>
        </div >
        <PlanRenewalModal
            isOpen={isProratedUpgradeOpen}
            onClose={() => setIsProratedUpgradeOpen(false)}
            companyProfile={companyInfo}
            token={token}
            onSuccess={() => {
                const API_URL_local = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
                    ? 'http://localhost:5000'
                    : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');
                fetch(`${API_URL_local}/api/company/profile`, { headers: { Authorization: `Bearer ${token}` } })
                    .then(r => r.json())
                    .then(d => { if (d.success) setCompanyInfo(d.data); })
                    .catch(() => {});
            }}
        />
        </>
    );
}

export default StaffPage;




